'use client'

import { FunctionComponent, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useMachine } from '@xstate/react'
import Image from 'next/image'
import moment from 'moment-timezone'
import { announce } from '@react-aria/live-announcer'
import classNames from 'classnames'
import { usePathname } from 'next/navigation'
import styles from './Timer.module.css'
import { getExpiresInFromExpiresAt, handleCountdownAlert } from '@/utils/components/Timer.utils'
import { timerMachine } from './timerMachine'
import ModalContext from '@/app/_providers/ModalProvider/ModalContext'
import { isConfirmationPage } from '@/utils/site.utils'
import { CLASSES } from '@/config/html-attributes.config'
import { updateBodyClass } from '@/utils/dom.utils'
import ThemeContext from '@/app/_providers/ThemeProvider/ThemeContext'
import { Portal } from '@/app/_components/Portal/Portal'
import { PORTAL_SELECTORS } from '@/config/navigation.config'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import TXT from '@/messages'

type Props = {
  handleRestart?: () => void
  isEvent?: boolean
  isEventList?: boolean
}

const Timer: FunctionComponent<Props> = ({
  handleRestart,
  isEvent = false,
  isEventList = false,
}) => {
  // Hooks
  const pathname = usePathname()
  const toggleLoadingBlob = useOurStore((store) => store.toggleLoadingBlob)
  const resetByGroups = useOurStore((store) => store.resetByGroups)
  const resetPartial = useOurStore((store) => store.resetPartial)
  // Refs
  const mounted = useRef(true)
  const timerRef = useRef<NodeJS.Timeout>(null)
  // Context
  const { cart } = useCart()
  const { updateModalState } = useContext(ModalContext)
  const { mobile } = useContext(ThemeContext)
  // Machine
  const [state, send] = useMachine(timerMachine)

  // State
  const getTime = useCallback(() => {
    if (cart.expiresAt) {
      return getExpiresInFromExpiresAt(cart.expiresAt, moment())
    }
    return 0
  }, [cart.expiresAt])

  const [time, setTime] = useState(getTime())
  const timerTimeoutRef = useRef<NodeJS.Timeout>(null)

  const isConfirmation = isConfirmationPage(pathname)

  // Clear any existing interval
  const clearExistingInterval = (): void => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }

  const handleExpired = useCallback((): void => {
    clearExistingInterval()
    toggleLoadingBlob(false, false)
    updateModalState({ open: { expiration: true } })
    resetByGroups(['cart'])
    resetPartial()
    handleRestart?.()
  }, [toggleLoadingBlob, resetByGroups, resetPartial, handleRestart])

  const handleStateTransitions = useCallback((): void => {
    const notInitialCart = cart.state !== 'NEW'
    const isActive = state.matches('active')

    // inactive state
    if (state.matches('inactive')) {
      clearExistingInterval()
      updateBodyClass(null, CLASSES.WITH_TIMER)

      if (notInitialCart && cart.expiresAt && cart.state === 'PENDING') {
        // If the cart is already expired when the timer mounts (e.g. returning from an
        // external payment page such as Affirm after the cart has expired), expire
        // immediately rather than starting a countdown. This matches the legacy app,
        // which always activates the timer for a PENDING cart and lets the expired
        // handler delete the cart and redirect to the start of the purchase flow.
        if (getTime() <= 0) {
          handleExpired()
          send({ type: 'EXPIRED' })
          return
        }
        if (timerTimeoutRef.current) {
          clearTimeout(timerTimeoutRef.current)
        }
        setTime(getTime())
        // Update time again before the next state is reached
        timerTimeoutRef.current = setTimeout(() => {
          if (mounted.current) {
            setTime(getTime())
          }
        }, 500)

        updateBodyClass(CLASSES.WITH_TIMER)
        send({ type: 'START' })
        return
      }
      return
    }

    // active state
    if (isActive) {
      // Handle cart state transitions
      if (cart.state === 'BOOKED') {
        send({ type: 'STOP' })
        return
      }

      if (cart.state === 'NEW') {
        send({ type: 'STOP' })
        return
      }

      if (cart.state === 'EXPIRED') {
        handleExpired()
        send({ type: 'EXPIRED' })
        return
      }

      // Normal timer operation
      setTime(getTime())
      timerRef.current = setInterval(() => {
        if (mounted.current) {
          const nextTime = getTime()
          if (!nextTime || nextTime <= 0) {
            handleExpired()
            send({ type: 'EXPIRED' })
          } else if (nextTime) {
            setTime(nextTime)
          }
        }
      }, 1000)
      return
    }

    // expired state
    if (state.matches('expired')) {
      send({ type: 'STOP' })
    }
  }, [cart.state, cart.expiresAt, state, getTime, send, handleExpired])

  useEffect(() => {
    clearExistingInterval()

    // Handle different states

    handleStateTransitions()

    // Cleanup function
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.state, state])

  // cleanup
  useEffect(() => {
    mounted.current = true
    return () => {
      clearExistingInterval()
      mounted.current = false
    }
  }, [])

  const minutes = Math.floor(time / 60)
  const seconds = time - minutes * 60

  useEffect(() => {
    const countdownAlert = handleCountdownAlert(minutes, seconds)
    if (state.matches('active') && countdownAlert) {
      announce(countdownAlert, 'polite')
    }
  }, [minutes, seconds, state])

  const baseTimerComponent = (
    <div
      data-testid="timer-wrapper"
      data-test-state={state.value}
      className={classNames(
        {
          [styles.EventsWrapper]: isEvent && !isConfirmation,
          [styles.List]: isEventList,
          [styles.Active]: state.matches('active'),
        },
      )}
    >
      {state.matches('active')
        ? (
          <div
            className={classNames('timer', styles.Timer, {
              [styles.Events]: isEvent,
              [styles.Started]: state.matches('active'),
              [styles.Active]: state.matches('active'),
            })}
          >
            <span>
              <Image
                width={13}
                height={13}
                className={styles.Clock}
                src="/static/svg/clock.svg"
                alt="clock"
              />
              <span className={styles.TabularNums} data-testid="timer-clock">
                {' '}
                {`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
                {' '}
              </span>
              {TXT.timer.minutesLeft}
            </span>
            <span>
              {TXT.timer.toPurchase}
            </span>

          </div>
        )
        : (
          <div className={styles.Placeholder} />
        )}
    </div>
  )
  if (mobile || isEvent) {
    return (
      <Portal selector={`#${PORTAL_SELECTORS.mobileTimer}`}>
        {baseTimerComponent}
      </Portal>
    )
  }
  return baseTimerComponent
}

export default Timer
